import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';
import { reaction } from 'mobx';
import { register } from '../i18n';
import { confirmDialog } from '../lessonPlayer/components/dialogs';
import { LessonMode, ScoringOption } from '../Constants';
import useLessonInit from '../hooks/useLessonInit';
import BackgroundShade from '../lessonPlayer/layout/BackgroundShade';
import ContentLayout from '../lessonPlayer/layout/ContentLayout';
import FooterLayout from '../lessonPlayer/layout/FooterLayout';
import InternetMessage from '../lessonPlayer/components/InternetMessage';
import FullpageLoader from '../lessonPlayer/components/FullpageLoader';
import HiddenAudioPlayer from '../lessonPlayer/components/HiddenAudioPlayer';
import ToolbarLayout from '../lessonPlayer/layout/ToolbarLayout';
import AccessibilityAnnouncer from '../lessonPlayer/components/AccessibilityAnnouncer';

// remove manuscript when all components are built and manuscript items are removed from
// preview, graded and review modes
import '../css/Player.scss';
import '../css/Manuscript.scss';
import '../css/TestItems.scss';
import '../css/CkeditorContentStyles.scss';
import '../css/Accessibility.scss';

const t = register('ActivityMode');
const t2 = register('GlobalQuestionLabels');

const ActivityMode = observer((props) => {
  const {
    lessonManager,
    questionFeedbackManager,
    toolbarManager,
  } = useContext(MobXProviderContext);

  const mode = props.mode || LessonMode.ACTIVITY_MODE;

  // Each time activeC2CTranslationId changes, increment this counter so the
  // lesson-window div gets a new key. React will unmount the old tree and mount
  // a fresh one, ensuring every register()-based component re-reads i18n with
  // the updated bundle immediately. A MobX reaction is used here because it
  // subscribes directly to the observable — more reliable than observer tracking
  // inside a conditional JSX branch.
  const [c2cRemountKey, setC2cRemountKey] = useState(0);

  useEffect(() => {
    const dispose = reaction(
      () => lessonManager.activeC2CTranslationId,
      () => setC2cRemountKey((n) => n + 1)
    );
    return dispose;
  }, [lessonManager]);

  useLessonInit(mode);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const className = `${LessonMode.ACTIVITY_MODE} theme-override`;
    const html = document.getElementsByTagName('html')[0];
    html.setAttribute('class', className);
  });

  useEffect(() => {
    const handleWindowMessage = (event) => {
      if (event.data && event.data.type === 'related_tab_button_visibility') {
        if (typeof event.data.show === 'boolean') {
          lessonManager.setShouldShowRelatedTabButton(event.data.show);
        }
        if (typeof event.data.open === 'boolean') {
          lessonManager.setIsSatelliteOpen(event.data.open);
        }
      }
    };

    if (window.addEventListener) {
      window.addEventListener('message', handleWindowMessage, false);
    } else {
      window.attachEvent('onmessage', handleWindowMessage);
    }

    return () => {
      if (window.removeEventListener) {
        window.removeEventListener('message', handleWindowMessage, false);
      } else {
        window.detachEvent('onmessage', handleWindowMessage);
      }
    };
  }, [lessonManager]);

  useEffect(() => {

    if (lessonManager.isLoaded && questionFeedbackManager.hasFeedback && questionFeedbackManager.questionFeedbackMode?.alertScoringOption && !showAlert) {
      setShowAlert(true);
      const { practiceAttempts } = questionFeedbackManager.questionFeedbackMode;
      const scoringOption = questionFeedbackManager.questionFeedbackMode.scoringOption || ScoringOption.FIRST;
      
      const dialogObject = {
        title: t('scoringAlertTitle'),
        text: t('scoringAlertMessage', {
          attempts: practiceAttempts,
          scoringOption: t2(scoringOption) || t2(ScoringOption.FIRST)
        }),
        showCancelButton: false,
        confirmButtonText: 'OK'
      };
      confirmDialog(dialogObject);

    }
  }, [lessonManager.isLoaded, lessonManager.lessonOptions, questionFeedbackManager.questionFeedbackMode]);

  return (
    <>
      <div id="ariaLiveMessage" aria-live="polite">{lessonManager.currentAriaMessage}</div>
      <AccessibilityAnnouncer />
      {/* eslint-disable-next-line no-nested-ternary */}
      {(lessonManager.isOnline && lessonManager.isLoaded && ((lessonManager.themeLoaded && lessonManager.translationsLoaded) || lessonManager.hasNoTheme)) ? (
        <div
          key={c2cRemountKey}
          className={classNames('lesson-window', 'Activity-Mode', { highlightOn: toolbarManager.isHighlightOrStickyToolOn })}
        >
          <BackgroundShade>
            <HiddenAudioPlayer/>
            <header aria-label={t('mainMenu')} role='navigation'>
              <ToolbarLayout/>
            </header>
            <main role='main'>
              <ContentLayout/>
            </main>
            <nav aria-label={t('footer')}>
              <FooterLayout/>
            </nav>
          </BackgroundShade>
        </div>
      ) : (!lessonManager.isOnline) ?
        (
          <InternetMessage/>
        )
        : (
          <FullpageLoader/>
        )}
    </>
  );
});
export default ActivityMode;
